import axios from 'axios';
import { addToQueue, replayQueue } from './offlineQueue';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 10000
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Auth error
    if (error.response?.status === 401) {
      const currentPath = window.location.pathname;
      const isAuthRequest = error.config?.url?.includes('/auth/login') || error.config?.url?.includes('/auth/register');
      if (currentPath !== '/login' && currentPath !== '/register' && !isAuthRequest) {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }

    // Network error on a mutation (PUT/POST/DELETE) → queue it
    const isNetworkError = !error.response && (error.code === 'ERR_NETWORK' || error.message === 'Network Error');
    const method = error.config?.method?.toUpperCase();
    const isMutation = ['PUT', 'POST', 'DELETE'].includes(method);

    // Don't queue file uploads (FormData) – too large for localStorage
    const isFormData = error.config?.data instanceof FormData;

    if (isNetworkError && isMutation && !isFormData && error.config) {
      addToQueue({
        method: error.config.method,
        url: error.config.url,
        data: error.config.data,
        label: buildLabel(error.config),
      });

      // Return a fake success so the UI doesn't break
      return Promise.resolve({
        data: { _offline: true, _message: 'Änderung wird synchronisiert sobald wieder online.' },
        status: 200,
        config: error.config,
      });
    }

    return Promise.reject(error);
  }
);

function buildLabel(config) {
  const method = config.method?.toUpperCase();
  const url = config.url || '';
  if (url.includes('/tickets') && method === 'PUT') return 'Ticket aktualisieren';
  if (url.includes('/tickets') && method === 'POST') return 'Ticket erstellen';
  if (url.includes('/comments') && method === 'POST') return 'Kommentar hinzufügen';
  if (url.includes('/work-reports') && method === 'POST') return 'Arbeitsbericht hinzufügen';
  if (method === 'DELETE') return 'Eintrag löschen';
  return `${method} ${url}`;
}

// Auto-sync when back online
window.addEventListener('online', () => {
  replayQueue(api).then(({ synced, failed }) => {
    if (synced > 0) {
      window.dispatchEvent(new CustomEvent('offlineSync', { detail: { synced, failed } }));
    }
  });
});

export default api;
